import { useState, useEffect } from "react";
import { authHeaders } from "../lib/auth";
import AppHeader from "./AppHeader";

// ── Colors (matches ProfileScreen / MyIntake) ─────────────────────────
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D",
  green:"#1E8A4C", warning:"#F29423", danger:"#D93025",
};

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';
// Phase 2: identity rides via authHeaders() (X-WTR-Auth session token).

// ── Icons ─────────────────────────────────────────────────────────────
function Icon({ name, size=20, color=C.blue }) {
  const s=size, ic=color;
  const icons = {
    ring: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="7" stroke={ic} strokeWidth="2"/></svg>,
    hub: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 8h14v9a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/><path d="M9 8V5h6v3" stroke={ic} strokeWidth="1.5"/><circle cx="12" cy="13" r="2" stroke={ic} strokeWidth="1.5"/></svg>,
    bottle: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M10 2h4v3l1.5 2.5a4 4 0 0 1 .5 2V20a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V9.5a4 4 0 0 1 .5-2L10 5V2Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    watch: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="7" y="7" width="10" height="10" rx="3" stroke={ic} strokeWidth="1.5"/><path d="M9 7l.5-3h5l.5 3M9 17l.5 3h5l.5-3" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    check: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={ic} strokeWidth="1.5"/><path d="M8 12l3 3 5-6" stroke={ic} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    lock: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="5" y="11" width="14" height="9" rx="2" stroke={ic} strokeWidth="1.5"/><path d="M8 11V8a4 4 0 0 1 8 0v3" stroke={ic} strokeWidth="1.5"/></svg>,
  };
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icons[name]||icons.ring}</span>;
}

// ── Shared card shell ─────────────────────────────────────────────────
function DeviceCard({ icon, title, sub, statusLabel, statusTone, children, dim }) {
  const toneColor = statusTone === 'on' ? C.green : statusTone === 'soon' ? C.muted : C.warning;
  const toneBg = statusTone === 'on' ? `${C.green}14` : statusTone === 'soon' ? C.bg : `${C.warning}14`;
  return (
    <div style={{
      background: C.white, borderRadius:18, border:`1.5px solid ${C.border}`,
      padding:"16px", opacity: dim ? 0.55 : 1,
    }}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <div style={{width:40,height:40,borderRadius:11,flexShrink:0,background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <Icon name={icon} size={21} color={C.blueDark}/>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15,fontWeight:800,color:C.body}}>{title}</div>
          <div style={{fontSize:11,color:C.muted,marginTop:1}}>{sub}</div>
        </div>
        {statusLabel && (
          <span style={{flexShrink:0,fontSize:10,fontWeight:800,letterSpacing:0.4,padding:"4px 9px",borderRadius:20,background:toneBg,color:toneColor,textTransform:"uppercase"}}>
            {statusLabel}
          </span>
        )}
      </div>
      {children && <div style={{marginTop:14}}>{children}</div>}
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────
export default function DevicesScreen({ onClose }) {
  const [email] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem('wtr_profile') || '{}'); return (p.email || '').trim().toLowerCase(); }
    catch(e) { return ''; }
  });

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Oura (Wave 2: OAuth2; no tokens ever pasted or stored client-side)
  const [ouraConnected, setOuraConnected] = useState(false);
  const [ouraSource, setOuraSource] = useState(null); // 'oauth' | 'legacy_token' | null
  const [ouraBusy, setOuraBusy] = useState(false);
  const [ouraProof, setOuraProof] = useState(null);
  const [ouraError, setOuraError] = useState("");

  // Hub
  const [hubInput, setHubInput] = useState("");
  const [hubLinked, setHubLinked] = useState(null);
  const [hubBusy, setHubBusy] = useState(false);
  const [hubError, setHubError] = useState("");
  const [hubEditing, setHubEditing] = useState(false);

  const validEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const hasName = !!(profile && profile.first_name && profile.last_name);

  // Bottle status is read from the signals WTRBottleScreen already persists:
  // wtr_btl_device is the remembered pairing {id,name}; wtr_btl_link_ts is a
  // heartbeat stamped on every live telemetry frame. We reflect that here rather
  // than re-implement BLE. A live "Connect" button belongs in a future build once
  // the BLE connect is extracted from WTRBottleScreen into a shared lib.
  const bottlePair = (() => {
    try { return JSON.parse(localStorage.getItem('wtr_btl_device') || 'null'); } catch(e) { return null; }
  })();
  const bottleLiveTs = (() => {
    try { return parseInt(localStorage.getItem('wtr_btl_link_ts') || '0', 10) || 0; } catch(e) { return 0; }
  })();
  const bottlePaired = !!(bottlePair && (bottlePair.name || bottlePair.id));
  const bottleConnected = bottlePaired && (Date.now() - bottleLiveTs < 60000);

  useEffect(() => {
    if (!validEmail) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/wtr/profile?email=${encodeURIComponent(email)}`, { headers: authHeaders() });
        const data = await res.json();
        if (!cancelled && data.ok && data.profile) {
          const p = data.profile;
          setProfile(p);
          setOuraConnected(!!p.oura_connected);
          setHubLinked(p.hub_device_id || null);
        }
      } catch(e) { /* leave defaults */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [email, validEmail]);

  // Oura OAuth status: the server is the source of truth for the connection.
  // Also surfaces the ?oura= result when the browser returns from Oura consent.
  useEffect(() => {
    if (!validEmail) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/wtr/oura/status`, { headers: authHeaders() });
        const d = await res.json();
        if (!cancelled && d && d.ok) {
          setOuraConnected(!!d.connected);
          setOuraSource(d.source || null);
        }
      } catch (e) { /* leave defaults */ }
    })();
    try {
      const q = new URLSearchParams(window.location.search).get("oura");
      if (q === "connected") { setOuraProof("Oura connected"); setOuraError(""); }
      if (q === "denied") setOuraError("Oura access was declined. You can try again anytime.");
      if (q === "error") setOuraError("Something went wrong connecting Oura. Try again.");
      if (q) window.history.replaceState({}, "", window.location.pathname);
    } catch (e) { /* non-fatal */ }
    return () => { cancelled = true; };
  }, [email, validEmail]);

  // Always send name with any save; the profile route requires it and preserves
  // every field we omit via COALESCE, so this never clobbers other data.
  async function saveProfile(patch) {
    const res = await fetch(`${API_BASE}/api/wtr/profile`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ email, first_name: profile.first_name, last_name: profile.last_name, ...patch }),
    });
    return res.json().then(d => ({ ok: res.ok, d }));
  }

  async function connectOura() {
    // Wave 2: OAuth2. Ask the Sales Engine for a signed authorize URL, then send
    // the browser to Oura's consent screen. Identity rides in the signed state;
    // the callback stores encrypted tokens server-side and redirects back here.
    setOuraBusy(true); setOuraError("");
    try {
      const res = await fetch(`${API_BASE}/api/wtr/oura/connect/init`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await res.json();
      if (!res.ok || !d.ok || !d.url) {
        setOuraError(d.error || 'Could not start the Oura connection. Try again.');
        setOuraBusy(false); return;
      }
      window.location.href = d.url;
    } catch (e) {
      setOuraError('Network error. Try again.');
      setOuraBusy(false);
    }
  }

  async function linkHub() {
    const id = hubInput.trim();
    if (!id) { setHubError('Enter your hub device ID'); return; }
    setHubBusy(true); setHubError("");
    try {
      const { ok, d } = await saveProfile({ hub_device_id: id });
      if (!ok || !d.ok) { setHubError(d.error || 'Could not save. Try again.'); setHubBusy(false); return; }
      setProfile(p => ({ ...p, hub_device_id: id }));
      setHubLinked(id);
      setHubEditing(false);
      setHubInput("");
    } catch(e) {
      setHubError('Network error. Try again.');
    }
    setHubBusy(false);
  }

  async function disconnectOura() {
    setOuraBusy(true); setOuraError("");
    try {
      const res = await fetch(`${API_BASE}/api/wtr/oura/disconnect`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await res.json();
      if (!res.ok || !d.ok) { setOuraError(d.error || 'Could not disconnect. Try again.'); setOuraBusy(false); return; }
      setOuraConnected(false);
      setOuraSource(null);
      setOuraProof(null);
    } catch (e) {
      setOuraError('Network error. Try again.');
    }
    setOuraBusy(false);
  }

  async function unlinkHub() {
    setHubBusy(true); setHubError("");
    try {
      const res = await fetch(`${API_BASE}/api/wtr/profile/disconnect`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ email, target: 'hub' }),
      });
      const d = await res.json();
      if (!res.ok || !d.ok) { setHubError(d.error || 'Could not unlink. Try again.'); setHubBusy(false); return; }
      setProfile(p => ({ ...p, hub_device_id: null }));
      setHubLinked(null);
      setHubEditing(false);
      setHubInput("");
    } catch(e) {
      setHubError('Network error. Try again.');
    }
    setHubBusy(false);
  }

  const inputStyle = {
    width:"100%", padding:"12px 14px", fontSize:14, fontWeight:600, color:C.body,
    background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12, outline:"none",
    fontFamily:"Nunito Sans,sans-serif", WebkitAppearance:"none",
  };
  const btnStyle = (busy) => ({
    width:"100%", padding:"13px 0", marginTop:10,
    background: busy ? C.bg : `linear-gradient(135deg,${C.blue},${C.blueDark})`,
    color: busy ? C.muted : "#fff", border:"none", borderRadius:12,
    fontSize:14, fontWeight:800, cursor: busy ? "default" : "pointer",
    fontFamily:"inherit", letterSpacing:0.2,
  });
  const linkBtn = {
    marginTop:12, background:"transparent", border:"none", color:C.blueDark,
    fontSize:12, fontWeight:800, cursor:"pointer", padding:0, fontFamily:"inherit",
  };

  if (loading) {
    return (
      <div style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:40,height:40,border:`3px solid ${C.border}`,borderTopColor:C.blue,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
          <div style={{fontSize:14,color:C.muted,fontWeight:600}}>Loading devices...</div>
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="devices-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}`}</style>

      <AppHeader
        variant="modal"
        title="Devices & Integrations"
        subtitle="Connect your data sources"
        onClose={onClose}
      />

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:16,paddingBottom:40,animation:"fadeIn 0.3s ease"}}>

        {!hasName && (
          <div style={{padding:"16px 18px",background:`${C.warning}10`,border:`1.5px solid ${C.warning}30`,borderRadius:16,fontSize:13,fontWeight:600,color:C.body}}>
            Set up your Profile first. Connections are saved to your profile, so we need your name and email before linking a device.
          </div>
        )}

        {/* OURA RING */}
        <DeviceCard
          icon="ring"
          title="Oura Ring"
          sub="Sleep, readiness, HRV, and recovery"
          statusLabel={ouraConnected ? "Connected" : "Not connected"}
          statusTone={ouraConnected ? "on" : "off"}
        >
          {ouraConnected ? (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:C.green}}>
                <Icon name="check" size={16} color={C.green}/>
                {ouraProof ? ouraProof : "Your Oura data is linked"}
              </div>
              {ouraSource === "legacy_token" && (
                <div style={{fontSize:11,color:C.muted,marginTop:7,lineHeight:1.5}}>
                  Connected with a legacy access token. Tap Upgrade to switch to the
                  new secure Oura sign-in. Your data continues either way.
                </div>
              )}
              <div style={{display:"flex",gap:16,alignItems:"center",marginTop:8}}>
                {ouraSource === "legacy_token" && (
                  <button style={linkBtn} disabled={ouraBusy} onClick={connectOura}>
                    {ouraBusy ? "Opening Oura..." : "Upgrade connection"}
                  </button>
                )}
                <button style={{...linkBtn, color:C.danger}} disabled={ouraBusy} onClick={disconnectOura}>
                  {ouraBusy ? "Disconnecting..." : "Disconnect"}
                </button>
              </div>
              {ouraError && <div style={{fontSize:12,fontWeight:600,color:C.danger,marginTop:8}}>{ouraError}</div>}
            </div>
          ) : (
            <div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>
                Connect through your Oura account. You will be sent to Oura to approve
                access, then returned here. No passwords or tokens to paste.
              </div>
              {ouraError && <div style={{fontSize:12,fontWeight:600,color:C.danger,marginTop:8}}>{ouraError}</div>}
              <button data-testid="oura-connect-btn" disabled={!hasName || ouraBusy} style={btnStyle(ouraBusy || !hasName)} onClick={connectOura}>
                {ouraBusy ? "Opening Oura..." : "Connect Oura"}
              </button>
            </div>
          )}
        </DeviceCard>

        {/* HOME WTR HUB */}
        <DeviceCard
          icon="hub"
          title="Home WTR Hub"
          sub="Link your hub to this account"
          statusLabel={hubLinked ? "Linked" : "Not linked"}
          statusTone={hubLinked ? "on" : "off"}
        >
          {hubLinked && !hubEditing ? (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:C.green}}>
                <Icon name="check" size={16} color={C.green}/>
                <span style={{fontFamily:"Nunito Sans,monospace",wordBreak:"break-all"}}>{hubLinked}</span>
              </div>
              <div style={{display:"flex",gap:16,alignItems:"center"}}>
                <button style={linkBtn} onClick={() => { setHubEditing(true); setHubError(""); }}>
                  Relink a different hub
                </button>
                <button style={{...linkBtn, color:C.danger}} disabled={hubBusy} onClick={unlinkHub}>
                  {hubBusy ? "Unlinking..." : "Unlink"}
                </button>
              </div>
              {hubError && <div style={{fontSize:12,fontWeight:600,color:C.danger,marginTop:8}}>{hubError}</div>}
            </div>
          ) : (
            <div>
              <input
                data-testid="hub-id-input"
                type="text"
                value={hubInput}
                onChange={(e) => { setHubInput(e.target.value); if (hubError) setHubError(""); }}
                placeholder="Hub device ID"
                disabled={!hasName || hubBusy}
                style={{...inputStyle, opacity: (!hasName) ? 0.6 : 1}}
              />
              <div style={{fontSize:11,color:C.muted,marginTop:7,lineHeight:1.5}}>
                Telemetry is already collected by Generosity. Linking associates a hub with your account so its readings power your hydration intelligence.
              </div>
              {hubError && <div style={{fontSize:12,fontWeight:600,color:C.danger,marginTop:8}}>{hubError}</div>}
              <button data-testid="hub-link-btn" disabled={!hasName || hubBusy} style={btnStyle(hubBusy || !hasName)} onClick={linkHub}>
                {hubBusy ? "Saving..." : hubLinked ? "Save hub" : "Link Hub"}
              </button>
              {hubEditing && (
                <button style={linkBtn} onClick={() => { setHubEditing(false); setHubInput(""); setHubError(""); }}>
                  Cancel
                </button>
              )}
            </div>
          )}
        </DeviceCard>

        {/* INTELLIGENT WTR BOTTLE */}
        <DeviceCard
          icon="bottle"
          title="Intelligent WTR Bottle"
          sub="Verified sips over Bluetooth"
          statusLabel={bottleConnected ? "Connected" : bottlePaired ? "Paired" : "Not paired"}
          statusTone={bottlePaired ? "on" : "soon"}
        >
          {bottlePaired ? (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:C.green}}>
                <Icon name="check" size={16} color={C.green}/>
                <span>{bottlePair.name || "Smart Cap"}{bottleConnected ? " \u00B7 live now" : ""}</span>
              </div>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.5,marginTop:8}}>
                Live readings and pairing controls are in the bottle experience.
              </div>
            </div>
          ) : (
            <div style={{fontSize:12,color:C.muted,lineHeight:1.5}}>
              Pairing connects over Bluetooth from the bottle experience. We are bringing pairing into this screen next.
            </div>
          )}
        </DeviceCard>

        {/* APPLE WATCH (hard wall) */}
        <DeviceCard
          icon="watch"
          title="Apple Watch"
          sub="Heart rate, activity, and workouts"
          statusLabel="iOS app"
          statusTone="soon"
          dim
        >
          <div style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:12,color:C.muted,lineHeight:1.5}}>
            <span style={{flexShrink:0,marginTop:1}}><Icon name="lock" size={14} color={C.muted}/></span>
            Apple Watch data comes through Apple Health, which is only reachable from a native iOS app. It will connect here once the Generosity iOS app is available.
          </div>
        </DeviceCard>

        {/* Footer note */}
        <div style={{fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.5,padding:"4px 8px"}}>
          Generosity{"\u2122"} uses your connected data to estimate hydration and recovery. It is not medical advice.
        </div>

      </div>
    </div>
  );
}
